import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardHeader, { DASHBOARD_HEADER_HEIGHT } from "./DashboardHeader";
import DashboardSidebar, { DASHBOARD_ICON_RAIL_WIDTH, DASHBOARD_SIDEBAR_WIDTH, } from "./DashboardSidebar";
import { useLayout } from "../contexts/LayoutContext";
import { fetchModules, selectModules, selectModulesLoaded, selectModulesLoading, selectPermissionDenied, selectPermissionDeniedMessage } from "../redux/userDataSlice";
import { selectIsAuthenticated, selectToken, selectIsSuperAdmin, selectEntityType, updateUserAndToken } from "../redux/authSlice";
import { setPresentationMode } from "../redux/presentationSlice";
import { ApiCall } from "../helper/axios";
import CommonLoader from "../common_components/CommonLoader";
import { isPathAuthorized } from "../utils/pathAuthorization";
import AccessRestrictedContent from "../common_components/AccessRestrictedContent";
/**
 * Dashboard shell (sidebar + header) which stays mounted for all protected pages.
 * Only the center `<Outlet />` content changes on navigation (no blink).
 *
 * Sizing derived from Figma:
 * - Header height: 70
 * - Icon rail: 60
 * - Expanded menu panel: 275 (total left = 335)
 */
export default function DashboardLayout() {
    const { isSidebarVisible, isSidebarExpanded, setIsSidebarExpanded } = useLayout();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const token = useSelector(selectToken);
    const isLoadingMenu = useSelector(selectModulesLoading);
    const modulesLoaded = useSelector(selectModulesLoaded);
    const modulesLoading = useSelector(selectModulesLoading);
    const permissionDenied = useSelector(selectPermissionDenied);
    const permissionDeniedMessage = useSelector(selectPermissionDeniedMessage);
    const location = useLocation();
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const modules = useSelector(selectModules);
    const isPresentationMode = useSelector((state) => state.presentation?.isPresentationMode);
    const permissions = useSelector((state) => state.auth?.permissions);
    const entityType = useSelector(selectEntityType);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    // Close sidebar on mobile when navigation occurs
    useEffect(() => {
        if (isMobile && isSidebarExpanded) {
            setIsSidebarExpanded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    const isAuthorized = React.useMemo(() => {
        if (!modulesLoaded || modulesLoading)
            return true;
        return isPathAuthorized(location.pathname, modules, isSuperAdmin, permissions, entityType);
    }, [location.pathname, modules, modulesLoaded, modulesLoading, isSuperAdmin, permissions, entityType]);
    // Fetch modules only once on mount when authenticated, not on every navigation
    const modulesFetchedRef = React.useRef(false);
    useEffect(() => {
        if (isAuthenticated && token && !modulesFetchedRef.current) {
            dispatch(fetchModules());
            ApiCall('GET', '/api/auth/me', null, { Authorization: token }).then(res => {
                const data = res?.data;
                if (data?.success) {
                    dispatch(updateUserAndToken({
                        user: data.data,
                        token: data.token || token,
                        permissions: data.data.permissions || {}
                    }));
                    if (data.data.presentationMode) {
                        dispatch(setPresentationMode({
                            isPresentationMode: true,
                            presentationClients: data.data.presentationClients || [],
                            presentationRoleId: data.data.presentation_role_id || null
                        }));
                    }
                }
            }).catch(() => { });
            modulesFetchedRef.current = true;
        }
        if (!isAuthenticated || !token) {
            modulesFetchedRef.current = false;
        }
    }, [dispatch, isAuthenticated, token]);
    const toggleSidebar = useCallback(() => {
        setIsSidebarExpanded(!isSidebarExpanded);
    }, [isSidebarExpanded, setIsSidebarExpanded]);
    const leftOffset = !isSidebarVisible
        ? 0
        : isSidebarExpanded
            ? DASHBOARD_ICON_RAIL_WIDTH + DASHBOARD_SIDEBAR_WIDTH
            : DASHBOARD_ICON_RAIL_WIDTH;
    return (_jsxs(_Fragment, { children: [isPresentationMode && (_jsx(Box, { sx: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: '4px solid #FF008E',
                    pointerEvents: 'none',
                    zIndex: 999999, // Ensure it sits on top of EVERYTHING
                } })), isLoadingMenu && !modulesLoaded ? _jsx(CommonLoader, {}) : _jsxs(Box, { sx: {
                    display: "flex",
                    minHeight: "100vh",
                    bgcolor: "background.default",
                }, children: [isSidebarVisible && (_jsxs(_Fragment, { children: [_jsx(DashboardHeader, { isSidebarExpanded: isSidebarExpanded, onToggleSidebar: toggleSidebar }), _jsx(DashboardSidebar, { isExpanded: isSidebarExpanded, onToggleExpanded: toggleSidebar })] })), _jsx(Box, { className: "content-container", "data-testid": "page-content", sx: {
                            flex: 1,
                            ml: `${leftOffset}px`,
                            pt: isSidebarVisible ? `${DASHBOARD_HEADER_HEIGHT}px` : 0,
                            minWidth: 0,
                        }, children: _jsx("div", { className: "main-container-body", children: isAuthenticated && token && !modulesLoaded ? (_jsx(Box, { sx: {
                                    minHeight: "calc(100vh - 200px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }, children: _jsx(CircularProgress, { sx: { color: "#d10075" } }) })) : (permissionDenied || !isAuthorized) ? (_jsx(Box, { sx: {
                                    minHeight: "calc(100vh - 200px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }, children: _jsx(AccessRestrictedContent, {}) })) : (_jsx(Outlet, {})) }) })] })] }));
}
